// client/src/App.js
import React, { useEffect, Suspense } from 'react';
import { createHashRouter, Navigate, Route, RouterProvider, Routes } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import toast from 'react-hot-toast';
import { AnimatePresence, MotionConfig } from 'framer-motion';
import socketService from './services/socketService';
import { CompatibilityRedirect, ContextNavigation, RequireAdmin, SidePanel, COMPATIBILITY_REDIRECTS } from './components/Navigation';
import Dashboard from './components/features/Dashboard/Dashboard';
import WeTakeoverPage from './pages/WeTakeoverPage';
import { ThemeProvider } from './contexts/theme/ThemeContext';
import { TwitterProvider } from './contexts/TwitterContext';
import { Web3Provider } from './contexts/Web3Context';
import { WorkloadProvider } from './contexts/WorkloadContext';
import { CreatorCacheProvider } from './contexts/CreatorCacheContext';
import { AuthProvider } from './contexts/auth/AuthContext';
import ProtectedRoute from './components/routing/ProtectedRoute';
import RequirePermission from './components/routing/RequirePermission';
import RouteRecovery from './components/routing/RouteRecovery';
import { DirtyNavigationProvider } from './components/routing/DirtyNavigationGuard';
import Login from './components/auth/Login';
import UserManagement from './components/features/Users/UserManagement';
import ConnectionStatus from './components/common/ConnectionStatus';
import { NavigationProvider, useNavigation } from './components/Navigation';
import { CommandPaletteProvider } from './components/ui/command-palette';
import { AgentChatProvider } from './contexts/AgentChatContext';
import { AgentSidebar } from './components/agent/AgentSidebar';
import BottomNav from './components/ui/BottomNav';
import { useMediaQuery } from './hooks/useMediaQuery';
import { OPS_PAGE_CONTRACTS } from './components/Navigation/constants/NavigationConfig';
import { publicLaunchHostToken } from './components/features/Campaigns/launch/publicLaunchHost';

// Core pages loaded eagerly for instant navigation
import CreatorManagement from './components/features/TwitterTracker/CreatorManagement';
import { WorkflowDashboard } from './components/features/Campaigns/workflow';
import CampaignDashboard from './components/features/Campaigns/CampaignDashboard';

// Lazy-load less frequent route components
const BrandManagement = React.lazy(() => import('./components/features/Brands/BrandManagement'));
const CampaignManagement = React.lazy(() => import('./components/features/Campaigns/CampaignManagement'));
const DealsManagement = React.lazy(() => import('./components/features/Deals/DealsManagement'));
const ReviewManagement = React.lazy(() => import('./components/features/Review/ReviewManagement'));
const CreatorWorkloadDashboard = React.lazy(() => import('./components/features/Workload/CreatorWorkloadDashboard'));
const PaymentsDashboard = React.lazy(() => import('./components/features/Payments/PaymentsDashboard'));
const PaymentHistory = React.lazy(() => import('./components/features/Payments/PaymentHistoryFixed'));
const SettingsMenu = React.lazy(() => import('./components/features/Settings/SettingsMenu'));
const ContentReviewPage = React.lazy(() => import('./components/features/ContentReview/ContentReviewPage'));
const BriefsPage = React.lazy(() => import('./components/features/Campaigns/BriefsPage'));
const LaunchListPage = React.lazy(() => import('./components/features/Campaigns/launch/LaunchListPage'));
const LaunchDashboardPage = React.lazy(() => import('./components/features/Campaigns/launch/LaunchDashboardPage'));
const PublicLaunchPage = React.lazy(() => import('./components/features/Campaigns/launch/PublicLaunchPage'));
const CreatorSelectionPage = React.lazy(() => import('./components/features/CreatorSelection/CreatorSelectionPage'));
const CreatorNotesFocus = React.lazy(() => import('./components/features/TwitterTracker/CreatorNotesFocus'));
const PendingCreatorsPage = React.lazy(() => import('./components/features/PendingCreators/PendingCreatorsPage'));
const TakeoverList = React.lazy(() => import('./components/features/TakeoverCampaigns/TakeoverList'));
const CreatorAnalyticsPage = React.lazy(() => import('./components/features/CreatorAnalytics/CreatorAnalyticsPage'));
const LaunchScoutPage = React.lazy(() => import('./components/features/LaunchScout/LaunchScoutPage'));

// Operations pages
const TransactionTracker = React.lazy(() => import('./components/features/Operations/TransactionTracker'));
const OpsCampaigns = React.lazy(() => import('./components/features/Operations/OpsCampaigns'));
const InvoiceManager = React.lazy(() => import('./components/features/Operations/InvoiceManager'));
const SalesPipeline = React.lazy(() => import('./components/features/Operations/SalesPipeline'));
const DealRecordPage = React.lazy(() => import('./components/features/Operations/salesCrm/DealRecordPage'));
const InvoiceLinkReview = React.lazy(() => import('./components/features/Operations/salesCrm/InvoiceLinkReview'));
const TeamTasksPage = React.lazy(() => import('./components/features/Operations/teamTasks/TeamTasksPage'));
const TaskRecordPage = React.lazy(() => import('./components/features/Operations/teamTasks/TaskRecordPage'));
const BDPipeline = React.lazy(() => import('./components/features/Operations/BDPipeline'));
const BrandDirectory = React.lazy(() => import('./components/features/Operations/BrandDirectory'));
const ProposalGenerator = React.lazy(() => import('./components/features/Operations/ProposalGenerator'));
const FeedbackBoard = React.lazy(() => import('./components/features/Operations/FeedbackBoard'));
const AiToolsPage = React.lazy(() => import('./components/features/AiTools/AiToolsPage'));
const ConnectPage = React.lazy(() => import('./components/features/AiTools/ConnectPage'));

function AppContent() {
  const { isCollapsed, isMobileView } = useNavigation();
  const isPhone = useMediaQuery('(max-width: 639px)');

  // Calculate margin based on sidebar state
  const getMainMargin = () => {
    if (isPhone) return 'ml-0'; // Phone: no sidebar, bottom nav instead
    if (isMobileView) return 'ml-16'; // Tablet: 64px icon sidebar
    return isCollapsed ? 'ml-16' : 'ml-[240px]'; // Desktop: 64px or compact 240px rail
  };

  // Initialize WebSocket connection when app loads
  useEffect(() => {
    // Connect to WebSocket server
    if (!socketService.isConnected()) {
      socketService.connect();
    }
    
    // Handle global Discord limit errors
    const handleDiscordLimitError = (data) => {
      const { message, campaignName } = data;
      
      let toastMessage = message || 'Discord server limit reached';
      if (campaignName) {
        toastMessage = `Campaign "${campaignName}": ${toastMessage}`;
      }

      // Show error toast with longer duration and action button
      toast.error(
        (t) => (
          <div className="flex flex-col gap-2">
            <span>{toastMessage}</span>
            <div className="flex gap-2 mt-2">
              <button
                onClick={() => {
                  // Open Discord server settings in new tab
                  window.open('https://discord.com/channels/@me', '_blank');
                  toast.dismiss(t.id);
                }}
                className="px-3 py-1 text-xs bg-surface-1 text-error rounded hover:bg-glass-bg-hover"
              >
                Open Discord
              </button>
              <button
                onClick={() => toast.dismiss(t.id)}
                className="px-3 py-1 text-xs bg-surface-1 text-error rounded hover:bg-glass-bg-hover"
              >
                Dismiss
              </button>
            </div>
          </div>
        ),
        {
          duration: 15000, // 15 seconds
          position: 'top-right',
          style: {
            maxWidth: '500px',
            background: 'var(--error)',
            color: '#fff',
          },
        }
      );
    };

    // Subscribe to global Discord limit error events
    socketService.on('discord:limit:reached', handleDiscordLimitError);
    
    // Clean up on unmount
    return () => {
      // Don't disconnect on unmount as other components might need the connection
      // Just clean up any listeners
      socketService.off('discord:limit:reached', handleDiscordLimitError);
    };
  }, []);

  return (
    <div className="flex h-screen bg-surface-0">
      {/* Hide sidebar completely on phone, show icon sidebar on tablet, full on desktop */}
      {!isPhone && <SidePanel />}
      <main className={`ui-motion-shell min-w-0 flex-1 overflow-auto bg-surface-0 mesh-gradient ${getMainMargin()} ${isPhone ? 'pb-14' : ''}`}>
        <ContextNavigation />
        <Suspense fallback={<div className="flex items-center justify-center h-full"><div className="animate-spin h-8 w-8 border-b-2 border-accent" /></div>}>
          <AnimatePresence mode="wait">
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/creators" element={<CreatorManagement />} />
              <Route path="/creators/pending" element={<RequirePermission permission="manage_creators"><PendingCreatorsPage /></RequirePermission>} />
              <Route path="/creator-notes" element={<RequirePermission permission="manage_creators"><CreatorNotesFocus /></RequirePermission>} />
              <Route path="/creator-analytics" element={<CreatorAnalyticsPage />} />
              <Route path="/creator-selection" element={<RequirePermission permission={['manage_creators', 'manage_campaigns']}><CreatorSelectionPage /></RequirePermission>} />
              <Route path="/launch-scout" element={<RequireAdmin><LaunchScoutPage /></RequireAdmin>} />
              <Route path="/brands" element={<BrandManagement />} />
              <Route path="/campaigns" element={<WorkflowDashboard />} />
              <Route path="/campaigns/legacy" element={<CampaignManagement />} />
              <Route path="/campaigns/:campaignId" element={<CampaignDashboard />} />
              <Route path="/launches" element={<LaunchListPage />} />
              <Route path="/launches/:launchId" element={<LaunchDashboardPage />} />
              <Route path="/deals" element={<DealsManagement />} />
              <Route path="/review" element={<ReviewManagement />} />
              <Route path="/users" element={<RequireAdmin><UserManagement /></RequireAdmin>} />
              <Route path="/settings/websockets" element={<Navigate to="/settings?tab=websockets" replace />} />
              <Route path="/workload-dashboard" element={<CreatorWorkloadDashboard />} />
              <Route path="/payments" element={<RequirePermission permission="manage_payments"><PaymentsDashboard /></RequirePermission>} />
              <Route path="/payments/history" element={<RequirePermission permission="manage_payments"><PaymentHistory /></RequirePermission>} />
              <Route path="/settings" element={<RequireAdmin><SettingsMenu /></RequireAdmin>} />
              <Route path="/content-review" element={<ContentReviewPage />} />
              <Route path="/briefs" element={<BriefsPage />} />
              <Route path="/content-review/:campaignId" element={<ContentReviewPage />} />
              <Route path="/takeover" element={<RequireAdmin><TakeoverList /></RequireAdmin>} />
              {/* Operations workspace routes — granular permissions */}
              <Route path="/ops/campaigns" element={<RequirePermission access={OPS_PAGE_CONTRACTS['ops-campaigns']}><OpsCampaigns /></RequirePermission>} />
              <Route path="/ops/sales" element={<RequirePermission access={OPS_PAGE_CONTRACTS['ops-sales']}><SalesPipeline /></RequirePermission>} />
              <Route path="/ops/sales/invoices" element={<RequirePermission access={OPS_PAGE_CONTRACTS['ops-sales']}><InvoiceLinkReview /></RequirePermission>} />
              <Route path="/ops/sales/campaign/:campaignId" element={<RequirePermission access={OPS_PAGE_CONTRACTS['ops-sales']}><DealRecordPage /></RequirePermission>} />
              <Route path="/ops/sales/invoice/:invoiceId" element={<RequirePermission access={OPS_PAGE_CONTRACTS['ops-sales']}><DealRecordPage /></RequirePermission>} />
              <Route path="/ops/sales/takeover/:takeoverId" element={<RequirePermission access={OPS_PAGE_CONTRACTS['ops-sales']}><DealRecordPage /></RequirePermission>} />
              <Route path="/ops/sales/:leadId" element={<RequirePermission access={OPS_PAGE_CONTRACTS['ops-sales']}><DealRecordPage /></RequirePermission>} />
              {/* Team tasks: open to every logged-in user, so no RequirePermission gate */}
              <Route path="/ops/tasks" element={<TeamTasksPage />} />
              <Route path="/ops/tasks/:taskId" element={<TaskRecordPage />} />
              <Route path="/ops/bd-pipeline" element={<RequirePermission access={OPS_PAGE_CONTRACTS['ops-bd']}><BDPipeline /></RequirePermission>} />
              <Route path="/ops/brands" element={<RequirePermission access={OPS_PAGE_CONTRACTS['ops-brands']}><BrandDirectory /></RequirePermission>} />
              <Route path="/ops/proposals" element={<RequirePermission access={OPS_PAGE_CONTRACTS['ops-proposals']}><ProposalGenerator /></RequirePermission>} />
              <Route path="/ops/feedback" element={<RequirePermission access={OPS_PAGE_CONTRACTS['ops-feedback']}><FeedbackBoard /></RequirePermission>} />
              <Route path="/ops/invoices" element={<RequirePermission access={OPS_PAGE_CONTRACTS['ops-invoices']}><InvoiceManager /></RequirePermission>} />
              <Route path="/ops/transactions" element={<RequirePermission access={OPS_PAGE_CONTRACTS['ops-transactions']}><TransactionTracker /></RequirePermission>} />
              {/* AI tools (platform MCP): open to every logged-in user */}
              <Route path="/ai-tools" element={<AiToolsPage />} />
              {COMPATIBILITY_REDIRECTS.map(({ from, to }) => (
                <Route key={from} path={from} element={<CompatibilityRedirect to={to} />} />
              ))}
              <Route path="*" element={<RouteRecovery variant="not-found" />} />
            </Routes>
          </AnimatePresence>
        </Suspense>
      </main>
      <AgentSidebar />
      {/* Bottom navigation for phones */}
      {isPhone && <BottomNav />}
    </div>
  );
}

function AppShell() {
  return (
    <MotionConfig reducedMotion="user">
    <DirtyNavigationProvider>
      <ThemeProvider>
        <NavigationProvider>
          <AuthProvider>
            <TwitterProvider>
              <Web3Provider>
                <WorkloadProvider>
                  <CreatorCacheProvider>
                    <AgentChatProvider>
                    <CommandPaletteProvider>
                    <div className="App">
                      <Routes>
                        <Route path="/login" element={<Login />} />
                        <Route path="/wetakeover" element={<WeTakeoverPage />} />
                        {/* OAuth consent for AI tools: full-page, no app shell; login returns here */}
                        <Route
                          path="/connect"
                          element={
                            <ProtectedRoute>
                              <Suspense fallback={null}>
                                <ConnectPage />
                              </Suspense>
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/*"
                          element={
                            <ProtectedRoute>
                              <AppContent />
                            </ProtectedRoute>
                          }
                        />
                      </Routes>
                      <Toaster
                        position="top-right"
                        toastOptions={{
                          duration: 5000,
                          style: {
                            borderRadius: '0',
                            padding: '12px 16px',
                            fontSize: '14px',
                            background: 'var(--surface-2)',
                            border: '1px solid var(--glass-border)',
                            color: 'var(--text-primary)',
                            boxShadow: 'var(--glass-shadow)',
                          },
                          success: {
                            style: {
                              borderLeft: '4px solid var(--success)',
                            },
                          },
                          error: {
                            style: {
                              borderLeft: '4px solid var(--error)',
                            },
                          },
                        }}
                      />
                      <ConnectionStatus />
                    </div>
                    </CommandPaletteProvider>
                    </AgentChatProvider>
                  </CreatorCacheProvider>
                </WorkloadProvider>
            </Web3Provider>
          </TwitterProvider>
        </AuthProvider>
        </NavigationProvider>
      </ThemeProvider>
    </DirtyNavigationProvider>
    </MotionConfig>
  );
}

/**
 * Public view-only launch page. Rendered outside AppShell on purpose: AuthProvider verifies a
 * stored token (and redirects to /login when that fails) and the other providers load team data,
 * none of which may happen on a page that outsiders open. Only the theme is shared.
 */
function PublicLaunchShell({ token }) {
  return (
    <MotionConfig reducedMotion="user">
      <ThemeProvider>
        <Suspense fallback={<div className="flex h-screen items-center justify-center bg-surface-0"><div className="animate-spin h-8 w-8 border-b-2 border-accent" /></div>}>
          <PublicLaunchPage token={token} />
        </Suspense>
      </ThemeProvider>
    </MotionConfig>
  );
}

// On launches.<domain> the app is only the public page: the token is the first path segment
// (bare domain = the sales pitch) and nothing else of the app is reachable there.
const publicHostToken = publicLaunchHostToken();

const router = createHashRouter(publicHostToken !== undefined ? [
  {
    path: '*',
    element: <PublicLaunchShell token={publicHostToken} />
  }
] : [
  {
    // No token: the sales pitch, as on the bare launches.* domain.
    path: '/public/launch',
    element: <PublicLaunchShell />
  },
  {
    path: '/public/launch/:token',
    element: <PublicLaunchShell />
  },
  {
    path: '*',
    element: <AppShell />
  }
]);

function App() {
  return <RouterProvider router={router} />;
}

export default App;
